import type { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { Checkmark, Close } from '@mint-lib/icons';
import { Button, Card, InputWithIcon } from '@mint-lib/tw-components';

import type { RagTableType } from '../../../../types/rag.js';
import { FileIcon } from '../FileIcon/FileIcon.jsx';

type Props = {
  item: RagTableType;
  fileName: RagTableType['fileName'];
  setFileName: Dispatch<SetStateAction<RagTableType['fileName']>>;
  handleRenameCancel: () => void;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
};

const TileRename = ({
  item,
  fileName,
  setFileName,
  handleRenameCancel,
  setIsEditing,
}: Props) => {
  const { baseName, extension } = parseFilename(fileName, item.fileType);

  const handleRename = () => {
    setFileName(baseName.trim() + extension);
    setIsEditing(false);
  };

  return (
    <Card
      variant={'outlined'}
      className="w-full"
      contentClassName="flex flex-row gap-2 items-center justify-start p-2"
    >
      <FileIcon type={item.fileType} />
      <div className="w-full">
        <InputWithIcon
          className="h-8"
          autoFocus
          size="small"
          value={baseName}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && baseName.trim().length) {
              e.preventDefault();
              handleRename();
            }
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setFileName(e.target.value + extension)
          }
          icon={
            <div className="flex flex-row gap-2">
              <Button
                icon
                variant="plain"
                disabled={!baseName.trim().length}
                size="small"
                aria-label="file-rename"
                onClick={handleRename}
              >
                <Checkmark />
              </Button>
              <Button
                icon
                variant="plain"
                size="small"
                aria-label="cancel-file-rename"
                onClick={handleRenameCancel}
              >
                <Close className="fill-error-dark" />
              </Button>
            </div>
          }
        />
      </div>
    </Card>
  );
};

const parseFilename = (fileName: string, fileType: string) => {
  if (fileType === 'folder') {
    return { baseName: fileName, extension: '' };
  } else {
    const lastDot = fileName.lastIndexOf('.');
    const baseName = lastDot === -1 ? fileName : fileName.slice(0, lastDot);
    const extension = lastDot === -1 ? '' : fileName.slice(lastDot);

    return { baseName, extension };
  }
};

export { TileRename };
